import { getMockTranscript, mockZoomRecordings } from '@src/mocks/zoomMockData';
import {
  addRecording,
  checkDatabaseHealth,
  clearVectorDB,
  getAllIndexedRecordings,
  getDatabaseStats,
  getIndexedRecordingIds,
  hybridSearch,
} from '@src/services/zoomVectorDB';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import VideoPreviewModal from './VideoPreviewModal';

// SET THIS TO false TO USE REAL ZOOM DATA
const USE_MOCK_DATA = false;

// Exponential backoff utility for API calls
const exponentialBackoff = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 5,
  baseDelay: number = 1000,
): Promise<T> => {
  let lastError: any;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Check if it's a rate limit error (429) or access error (403/401)
      const status = error.status || error.response?.status;
      const isRetryable =
        status === 429 ||
        status === 403 ||
        status === 401 ||
        status === 503 ||
        status === 502;

      if (!isRetryable && status) {
        throw error; // Don't retry non-retryable errors
      }

      if (i < maxRetries - 1) {
        const delay = Math.min(
          baseDelay * Math.pow(2, i) + Math.random() * 1000,
          30000,
        ); // Max 30s delay
        console.log(
          `Retrying after ${Math.round(delay)}ms... (attempt ${
            i + 2
          }/${maxRetries})`,
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

interface ZoomRecording {
  id: string;
  topic: string;
  start_time: string;
  duration: number;
  total_size: number;
  recording_count: number;
  share_url: string;
  has_video: boolean;
  has_transcript: boolean;
  recording_files?: any[];
  password?: string;
}

interface SearchResult {
  recording: ZoomRecording;
  highlightedSnippet?: string;
  score?: number;
}

interface Props {
  onSelectRecording: (recording: ZoomRecording) => void;
}

const ZoomRecordingsEnhanced = ({ onSelectRecording }: Props) => {
  const [recordings, setRecordings] = useState<ZoomRecording[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isIndexing, setIsIndexing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [indexedCount, setIndexedCount] = useState(0);
  const [totalWithTranscripts, setTotalWithTranscripts] = useState(0);
  const [previewRecording, setPreviewRecording] =
    useState<ZoomRecording | null>(null);
  const { userIdentityStore } = useStore();
  const datoPerson = useDatoPerson();

  useEffect(() => {
    // Load cached recordings immediately
    loadCachedRecordings();
    // Then fetch fresh data from server
    fetchRecordings();
  }, []);

  const loadCachedRecordings = async () => {
    try {
      const cachedRecordings = await getAllIndexedRecordings();
      if (cachedRecordings.length > 0) {
        console.log(
          `Loaded ${cachedRecordings.length} recordings from IndexedDB`,
        );
        setRecordings(cachedRecordings);
        setSearchResults(cachedRecordings.map(r => ({ recording: r })));

        // Count recordings with transcripts
        const withTranscripts = cachedRecordings.filter(
          (r: ZoomRecording) => r.has_transcript,
        ).length;
        setTotalWithTranscripts(withTranscripts);

        // Set loading to false since we have data to display
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading cached recordings:', error);
    }
  };

  const fetchRecordings = async () => {
    // Only show loading if we don't have cached data
    if (recordings.length === 0) {
      setIsLoading(true);
    }
    setError(null);

    try {
      let recs: ZoomRecording[] = [];

      if (USE_MOCK_DATA) {
        // Use mock data
        console.log('Using mock Zoom recordings data');
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
        recs = mockZoomRecordings;
      } else {
        // Fetch real data from API
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const params = new URLSearchParams();
        if (datoPerson?.person?.id) {
          params.append('personId', datoPerson.person.id);
        }
        const identity = userIdentityStore.currentIdentity;
        if (identity?.email) {
          params.append('email', identity.email);
        }

        const data = await exponentialBackoff(
          async () => {
            const response = await fetch(
              `${apiUrl}/api/zoom/list-recordings?${params.toString()}`,
              {
                headers: {
                  Authorization: `Bearer ${userIdentityStore.currentIdentity?.token}`,
                },
              },
            );

            if (!response.ok) {
              const error: any = new Error(
                `Failed to fetch recordings: ${response.status} ${response.statusText}`,
              );
              error.status = response.status;
              throw error;
            }

            return await response.json();
          },
          5,
          2000,
        ); // 5 retries with 2 second base delay
        recs = data.recordings || [];
      }

      // Only update recordings if we got new data (not on auth failure)
      setRecordings(recs);
      setSearchResults(recs.map(r => ({ recording: r })));

      // Count recordings with transcripts
      const withTranscripts = recs.filter(
        (r: ZoomRecording) => r.has_transcript,
      ).length;
      setTotalWithTranscripts(withTranscripts);

      // Start indexing in the background
      if (recs.length > 0) {
        indexRecordings(recs);
      }
    } catch (err: any) {
      // Check if it's an auth error (401)
      if (err.status === 401 || err.message.includes('401')) {
        // Don't show error to user - they can still use cached recordings
        console.warn('Zoom auth expired, keeping cached recordings:', err);
        // Don't overwrite recordings - keep cached data available
      } else {
        setError(err.message);
        console.error('Failed to fetch Zoom recordings:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const indexRecordings = async (recordingsList: ZoomRecording[]) => {
    setIsIndexing(true);
    setIndexedCount(0);

    try {
      // Don't clear database - we want to persist it
      // await clearVectorDB();

      // Get already indexed recordings to avoid re-indexing
      const existingRecordings = await getIndexedRecordingIds();

      // Get database stats and check health
      const stats = await getDatabaseStats();
      const health = await checkDatabaseHealth();

      console.log('Database stats:', {
        recordings: stats.totalRecordings,
        withTranscripts: stats.recordingsWithTranscripts,
        embeddings: stats.totalEmbeddings,
        sizeMB: stats.estimatedSizeMB,
        avgTranscriptLength: stats.averageTranscriptLength,
      });

      if (health.needsCleanup) {
        console.warn('Database needs cleanup:', health.reason);
        console.log('Recommendation:', health.recommendation);
      }

      // Smart transcript fetching based on database size and health
      const maxTranscripts = 100; // Maximum transcripts to store
      const shouldFetchTranscript =
        stats.recordingsWithTranscripts < maxTranscripts &&
        stats.estimatedSizeMB < 40 && // Also check size limit
        !health.needsCleanup; // Don't fetch more if cleanup needed

      if (USE_MOCK_DATA) {
        // Index mock recordings with their transcripts
        for (const recording of recordingsList) {
          try {
            // Skip if already indexed
            if (existingRecordings.has(recording.id)) {
              console.log(
                `Skipping already indexed mock recording: ${recording.topic}`,
              );
              setIndexedCount(prev => prev + 1);
              continue;
            }
            let transcript = '';

            // Get mock transcript if available
            if (recording.has_transcript) {
              const vttContent = getMockTranscript(recording.id);
              // Parse VTT to extract text
              transcript = vttContent
                .split('\n')
                .filter(
                  line =>
                    !line.includes('-->') &&
                    !line.startsWith('WEBVTT') &&
                    line.trim() !== '' &&
                    !/^\d+$/.test(line),
                )
                .join(' ')
                .trim();
            }

            // Add recording with transcript to vector DB
            await addRecording(recording, transcript);
            setIndexedCount(prev => prev + 1);

            // Simulate processing delay for better UX
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (recordingError) {
            console.error(
              `Failed to index recording ${recording.topic}:`,
              recordingError,
            );
          }
        }
      } else {
        // Fetch real transcripts from API
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const params = new URLSearchParams();
        if (datoPerson?.person?.id) {
          params.append('personId', datoPerson.person.id);
        }
        const identity = userIdentityStore.currentIdentity;
        if (identity?.email) {
          params.append('email', identity.email);
        }

        // Index each recording
        for (const recording of recordingsList) {
          try {
            console.log('Indexing recording:', {
              id: recording.id,
              topic: recording.topic,
              has_video: recording.has_video,
              has_transcript: recording.has_transcript,
              recording_files: recording.recording_files?.length || 0,
            });

            // Skip if already indexed
            if (existingRecordings.has(recording.id)) {
              console.log(
                `Skipping already indexed recording: ${recording.topic}`,
              );
              setIndexedCount(prev => prev + 1);
              continue;
            }

            // Add basic recording info first
            await addRecording(recording, '');

            // If it has a transcript, fetch and index it (with limits)
            if (
              recording.has_transcript &&
              recording.recording_files &&
              shouldFetchTranscript
            ) {
              const transcriptFile = recording.recording_files.find((f: any) =>
                ['TRANSCRIPT', 'VTT', 'SRT'].includes(f.file_type),
              );

              if (transcriptFile && transcriptFile.download_url) {
                try {
                  const transcriptParams = new URLSearchParams(params);
                  transcriptParams.append('recordingId', recording.id);
                  transcriptParams.append(
                    'fileUrl',
                    transcriptFile.download_url,
                  );

                  const transcriptData = await exponentialBackoff(
                    async () => {
                      const transcriptResponse = await fetch(
                        `${apiUrl}/api/zoom/get-transcript?${transcriptParams.toString()}`,
                        {
                          headers: {
                            Authorization: `Bearer ${userIdentityStore.currentIdentity?.token}`,
                          },
                        },
                      );

                      if (!transcriptResponse.ok) {
                        const error: any = new Error(
                          `Failed to fetch transcript: ${transcriptResponse.status}`,
                        );
                        error.status = transcriptResponse.status;
                        throw error;
                      }

                      return await transcriptResponse.json();
                    },
                    3,
                    3000,
                  ); // 3 retries with 3 second base delay for transcripts

                  // Update the recording with transcript
                  await addRecording(recording, transcriptData.transcript);
                } catch (transcriptError) {
                  console.warn(
                    `Failed to fetch transcript for ${recording.topic}:`,
                    transcriptError,
                  );
                }
              }
            }

            setIndexedCount(prev => prev + 1);
          } catch (recordingError) {
            console.error(
              `Failed to index recording ${recording.topic}:`,
              recordingError,
            );
            console.error('Recording data that failed:', recording);
          }
        }
      }
    } catch (err) {
      console.error('Failed to index recordings:', err);
    } finally {
      setIsIndexing(false);
    }
  };

  const performSearch = useCallback(
    async (query: string) => {
      if (query.trim() === '') {
        setSearchResults(recordings.map(r => ({ recording: r })));
        return;
      }

      console.log('🔍 Searching for:', query);
      setIsSearching(true);
      try {
        // Try semantic search first
        const results = await hybridSearch(query, 20);

        if (results.length > 0) {
          // Use semantic search results
          console.log(
            '✅ SEMANTIC SEARCH RESULTS:',
            results.length,
            'matches found',
          );
          console.log('Top 3 results:');
          results.slice(0, 3).forEach((result, index) => {
            console.log(
              `  ${index + 1}. "${result.recording.topic}" (score: ${
                result.score?.toFixed(3) || 'N/A'
              })`,
            );
            if (result.highlightedSnippet) {
              console.log(
                `     Snippet: "${result.highlightedSnippet.substring(
                  0,
                  100,
                )}..."`,
              );
            }
          });

          const searchResultsWithSnippets = results.map(result => {
            const recording = recordings.find(
              r => r.id === result.recording.recordingId,
            );
            return {
              recording: recording || (result.recording as any),
              highlightedSnippet: result.highlightedSnippet,
              score: result.score,
            };
          });
          setSearchResults(searchResultsWithSnippets);
        } else {
          // Fallback to text search if no semantic results
          console.log(
            '⚠️ No semantic search results, falling back to TEXT SEARCH',
          );
          const filtered = recordings.filter(
            rec =>
              rec.topic.toLowerCase().includes(query.toLowerCase()) ||
              formatDate(rec.start_time)
                .toLowerCase()
                .includes(query.toLowerCase()),
          );
          console.log(
            '📝 TEXT SEARCH RESULTS:',
            filtered.length,
            'matches found',
          );
          if (filtered.length > 0) {
            console.log('Matching recordings:');
            filtered.slice(0, 3).forEach((rec, index) => {
              console.log(
                `  ${index + 1}. "${rec.topic}" (${formatDate(
                  rec.start_time,
                )})`,
              );
            });
          }
          setSearchResults(filtered.map(r => ({ recording: r })));
        }
      } catch (err) {
        console.error('Search failed, falling back to text search:', err);
        // Error fallback to simple text search
        const filtered = recordings.filter(
          rec =>
            rec.topic.toLowerCase().includes(query.toLowerCase()) ||
            formatDate(rec.start_time)
              .toLowerCase()
              .includes(query.toLowerCase()),
        );
        setSearchResults(filtered.map(r => ({ recording: r })));
      } finally {
        setIsSearching(false);
      }
    },
    [recordings],
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, performSearch]);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatSize = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb > 1) {
      return `${gb.toFixed(1)} GB`;
    }
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSelect = (recording: ZoomRecording) => {
    setSelectedId(recording.id);

    // Log all available video files
    console.log('=== Available Video Files for Recording ===');
    console.log('Recording:', recording.topic);
    console.log('Total files:', recording.recording_files?.length || 0);

    if (recording.recording_files) {
      recording.recording_files.forEach((file: any, index: number) => {
        console.log(`\nFile ${index + 1}:`);
        console.log('  Type:', file.file_type);
        console.log('  Status:', file.status);
        console.log('  Size:', formatSize(file.file_size || 0));
        console.log('  Recording Type:', file.recording_type);

        if (file.file_type === 'MP4') {
          console.log('  📹 VIDEO FILE DETAILS:');
          console.log('    - Download URL:', file.download_url);
          console.log('    - Play URL:', file.play_url);
          console.log('    - Recording Start:', file.recording_start);
          console.log('    - Recording End:', file.recording_end);
        }

        if (file.file_type === 'M4A') {
          console.log('  🎵 AUDIO FILE');
        }

        if (['TRANSCRIPT', 'VTT', 'SRT', 'CHAT'].includes(file.file_type)) {
          console.log('  📝 TEXT/TRANSCRIPT FILE');
        }
      });

      // Show which MP4 file would be selected based on priority
      const mp4Files = recording.recording_files.filter(
        (f: any) => f.file_type === 'MP4' && f.status === 'completed',
      );

      if (mp4Files.length > 0) {
        // Same priority order as the API
        const recordingTypePriority = [
          'shared_screen_with_speaker_view', // Best: screen + speaker
          'shared_screen_with_gallery_view', // Good: screen + gallery
          'active_speaker', // OK: active speaker view
          'speaker_view', // OK: speaker view
          'shared_screen', // Fallback: screen only
          'gallery_view', // Fallback: gallery view
        ];

        const sortedMp4Files = mp4Files.sort((a: any, b: any) => {
          const aPriority = recordingTypePriority.indexOf(a.recording_type);
          const bPriority = recordingTypePriority.indexOf(b.recording_type);
          const aIndex = aPriority === -1 ? 999 : aPriority;
          const bIndex = bPriority === -1 ? 999 : bPriority;
          return aIndex - bIndex;
        });

        const selectedVideo = sortedMp4Files[0];
        console.log('\n📊 VIDEO SELECTION PRIORITY:');
        console.log(
          '  Available types:',
          mp4Files.map((f: any) => f.recording_type),
        );
        console.log('  ✅ SELECTED FOR IMPORT:', selectedVideo.recording_type);
        console.log(
          '  Priority rank:',
          recordingTypePriority.indexOf(selectedVideo.recording_type) + 1,
        );
      } else {
        console.log('\n⚠️ NO COMPLETED MP4 FILE FOUND');
      }
    }
    console.log('=====================================\n');

    onSelectRecording(recording);
  };

  const handleClearDatabase = async () => {
    if (
      window.confirm(
        'This will clear all cached recordings and search data. You will need to refresh to re-index recordings. Continue?',
      )
    ) {
      try {
        console.log('Clearing local index database...');
        await clearVectorDB();

        // Clear local state
        setRecordings([]);
        setSearchResults([]);
        setSearchTerm('');
        setIndexedCount(0);
        setTotalWithTranscripts(0);

        console.log('Local index cleared successfully');

        // Fetch fresh recordings
        await fetchRecordings();
      } catch (error) {
        console.error('Failed to clear database:', error);
        alert('Failed to clear the local index. Please try again.');
      }
    }
  };

  const getVideoUrl = (recording: ZoomRecording): string => {
    if (recording.recording_files) {
      const videoFile = recording.recording_files.find(
        (f: any) => f.file_type === 'MP4' && f.status === 'completed',
      );
      if (videoFile) {
        console.log('- download_url:', videoFile.download_url);
        console.log('- play_url:', videoFile.play_url);
      }
    }

    // For mock data, use the play_url directly
    if (USE_MOCK_DATA && recording.recording_files) {
      const videoFile = recording.recording_files.find(
        (f: any) => f.file_type === 'MP4' && f.status === 'completed',
      );
      if (videoFile && videoFile.play_url) {
        return videoFile.play_url;
      }
    }

    // For real Zoom recordings, use download_url from recording_files
    if (!USE_MOCK_DATA && recording.recording_files) {
      const videoFile = recording.recording_files.find(
        (f: any) => f.file_type === 'MP4' && f.status === 'completed',
      );
      if (videoFile && videoFile.download_url) {
        console.log(
          'Using download_url for video preview:',
          videoFile.download_url,
        );
        return videoFile.download_url;
      }
    }

    // Fallback to share_url if no download_url available
    if (recording.share_url) {
      console.log('Falling back to share_url:', recording.share_url);
      return recording.share_url;
    }

    console.log('No video URL found for recording');
    return '';
  };

  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query) return text;

    const words = query.toLowerCase().split(/\s+/);
    let highlighted = text;

    words.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      highlighted = highlighted.replace(regex, '<mark>$1</mark>');
    });

    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingMessage>Loading your Zoom recordings...</LoadingMessage>
      </Container>
    );
  }

  // Only show error-only screen if we have no cached recordings
  // If we have cached recordings, show them with an error banner
  if (error && recordings.length === 0) {
    return (
      <Container>
        <ErrorMessage>
          Failed to load recordings: {error}
          <RetryButton onClick={fetchRecordings}>Try Again</RetryButton>
        </ErrorMessage>
      </Container>
    );
  }

  if (recordings.length === 0) {
    return (
      <Container>
        <EmptyMessage>
          No cloud recordings found in your Zoom account.
          <HelpText>
            Make sure you have cloud recording enabled and have recorded
            meetings in the last 30 days.
          </HelpText>
        </EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      {USE_MOCK_DATA && (
        <MockBanner>
          🎭 Using Mock Data for Testing - Set USE_MOCK_DATA to false for real
          data
        </MockBanner>
      )}
      <SearchContainer>
        <SearchIcon>🔍</SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search recordings... (Try: 'budget planning' or 'AI integration')"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <ClearButton onClick={() => setSearchTerm('')}>×</ClearButton>
        )}
        <RefreshButton onClick={fetchRecordings} title="Refresh recordings">
          ⟳
        </RefreshButton>
        <ClearDatabaseButton
          onClick={handleClearDatabase}
          title="Clear local index (removes all cached recordings and search data)"
        >
          🗑️
        </ClearDatabaseButton>
      </SearchContainer>

      {isIndexing && (
        <IndexingStatus>
          🔄 Indexing recordings for smart search... ({indexedCount}/
          {recordings.length})
          {totalWithTranscripts > 0 && (
            <span> | {totalWithTranscripts} with transcripts</span>
          )}
        </IndexingStatus>
      )}

      {isSearching && <SearchingStatus>🔍 Searching...</SearchingStatus>}

      {searchResults.length === 0 && searchTerm ? (
        <NoResults>
          No recordings found matching "{searchTerm}"
          <HelpText>Try different keywords or a broader search term</HelpText>
        </NoResults>
      ) : (
        <RecordingsList>
          {searchResults.map(({ recording, highlightedSnippet }) => (
            <RecordingItem
              key={recording.id}
              selected={selectedId === recording.id}
            >
              <RecordingContent onClick={() => handleSelect(recording)}>
                <RecordingInfo>
                  <RecordingHeader>
                    <RecordingTitle>{recording.topic}</RecordingTitle>
                  </RecordingHeader>
                  <RecordingMeta>
                    <MetaItem>📅 {formatDate(recording.start_time)}</MetaItem>
                    <MetaItem>⏱ {formatDuration(recording.duration)}</MetaItem>
                    <MetaItem>💾 {formatSize(recording.total_size)}</MetaItem>
                  </RecordingMeta>
                  {highlightedSnippet && (
                    <SnippetContainer>
                      <SnippetLabel>Relevant excerpt:</SnippetLabel>
                      <Snippet>
                        {highlightText(highlightedSnippet, searchTerm)}
                      </Snippet>
                    </SnippetContainer>
                  )}
                  <RecordingFeatures>
                    {recording.has_video && <Feature success>✓ Video</Feature>}
                    {recording.has_transcript && (
                      <Feature success>✓ Transcript</Feature>
                    )}
                    {recording.recording_count > 1 && (
                      <Feature>{recording.recording_count} files</Feature>
                    )}
                  </RecordingFeatures>
                </RecordingInfo>
                <SelectButton selected={selectedId === recording.id}>
                  {selectedId === recording.id ? 'Selected' : 'Select'}
                </SelectButton>
              </RecordingContent>
            </RecordingItem>
          ))}
        </RecordingsList>
      )}

      {searchResults.length > 0 && (
        <FooterNote>
          Showing {searchResults.length} of {recordings.length} recordings
          {indexedCount > 0 && (
            <span> | {indexedCount} indexed with transcripts</span>
          )}
        </FooterNote>
      )}

      {previewRecording && (
        <VideoPreviewModal
          isOpen={!!previewRecording}
          onClose={() => setPreviewRecording(null)}
          videoUrl={getVideoUrl(previewRecording)}
          title={previewRecording.topic}
          password={previewRecording.password}
          isZoomUrl={!USE_MOCK_DATA}
          userEmail={userIdentityStore.currentIdentity?.email}
          personId={datoPerson?.person?.id}
          authToken={userIdentityStore.currentIdentity?.token}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MockBanner = styled.div`
  padding: 10px;
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.1),
    rgba(69, 212, 131, 0.1)
  );
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  text-align: center;
  color: #ffc107;
  font-weight: 600;
  font-size: 13px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #1a1a2e;
  border: 1px solid #484848;
  border-radius: 8px;
  transition: all 0.2s;

  &:focus-within {
    border-color: #45d483;
    box-shadow: 0 0 0 1px rgba(69, 212, 131, 0.2);
  }
`;

const SearchIcon = styled.div`
  font-size: 18px;
  color: #808080;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  padding: 0;
  color: #f3e9d7;
  font-size: 14px;

  &::placeholder {
    color: #606060;
  }

  &:focus {
    outline: none;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: #808080;
  font-size: 20px;
  cursor: pointer;
  padding: 0 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #f3e9d7;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: #808080;
  border: none;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(69, 212, 131, 0.1);
    color: #45d483;
  }
`;

const ClearDatabaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: #808080;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 87, 87, 0.1);
    color: #ff5757;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const IndexingStatus = styled.div`
  padding: 8px 12px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  color: #ffc107;
  font-size: 13px;
  text-align: center;
`;

const SearchingStatus = styled.div`
  padding: 8px 12px;
  background: rgba(69, 212, 131, 0.1);
  border: 1px solid rgba(69, 212, 131, 0.3);
  border-radius: 6px;
  color: #45d483;
  font-size: 13px;
  text-align: center;
`;

const NoResults = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #808080;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecordingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 420px; /* Approximately 3 recording items */
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a2e;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #484848;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #606060;
  }
`;

const RecordingItem = styled.div<{ selected: boolean }>`
  background: ${props =>
    props.selected ? 'rgba(69, 212, 131, 0.1)' : '#1a1a2e'};
  border: 1px solid ${props => (props.selected ? '#45d483' : '#484848')};
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #45d483;
    background: rgba(69, 212, 131, 0.05);
  }
`;

const RecordingContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
`;

const RecordingInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecordingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const RecordingTitle = styled.div`
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
`;

const RecordingMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  color: #a0a0a0;
  font-size: 12px;
`;

const SnippetContainer = styled.div`
  margin: 8px 0;
  padding: 10px;
  background: rgba(69, 212, 131, 0.05);
  border-left: 2px solid rgba(69, 212, 131, 0.3);
  border-radius: 4px;
`;

const SnippetLabel = styled.div`
  color: #45d483;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const Snippet = styled.div`
  color: #d0d0d0;
  font-size: 12px;
  line-height: 1.5;

  mark {
    background: rgba(255, 193, 7, 0.3);
    color: #ffc107;
    font-weight: 600;
    padding: 1px 2px;
    border-radius: 2px;
  }
`;

const RecordingFeatures = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Feature = styled.span<{ success?: boolean }>`
  padding: 2px 8px;
  background: ${props =>
    props.success ? 'rgba(69, 212, 131, 0.2)' : 'rgba(160, 160, 160, 0.2)'};
  color: ${props => (props.success ? '#45d483' : '#a0a0a0')};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const SelectButton = styled.button<{ selected?: boolean }>`
  padding: 8px 16px;
  background: ${props => (props.selected ? '#45d483' : '#6c757d')};
  color: ${props => (props.selected ? '#03041a' : '#fff')};
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => (props.selected ? '#3bc372' : '#5a6268')};
  }
`;

const LoadingMessage = styled.div`
  color: #a0a0a0;
  text-align: center;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  text-align: center;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: #45d483;
  border: 1px solid #45d483;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: rgba(69, 212, 131, 0.1);
  }
`;

const EmptyMessage = styled.div`
  color: #a0a0a0;
  text-align: center;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #808080;
  max-width: 300px;
  margin: 0 auto;
`;

const FooterNote = styled.div`
  font-size: 11px;
  color: #606060;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #484848;
`;

export default ZoomRecordingsEnhanced;
